import { Card, Text, Select, Group, Stack } from '@mantine/core'
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts'
import { metricsApi } from '@/api'
import { formatBytes } from '@/utils/format'
import { subHours, format } from 'date-fns'

const timeRanges = [
  { value: '1h', label: 'Last hour' },
  { value: '6h', label: 'Last 6 hours' },
  { value: '24h', label: 'Last 24 hours' },
  { value: '7d', label: 'Last 7 days' },
]

export function ClusterMetrics() {
  const [timeRange, setTimeRange] = useState('1h')

  const { data: metrics = [], isLoading } = useQuery({
    queryKey: ['metrics', 'cluster', timeRange],
    queryFn: async () => {
      const hours = timeRange === '1h' ? 1 : timeRange === '6h' ? 6 : timeRange === '24h' ? 24 : 168
      const end = new Date()
      const start = subHours(end, hours)
      
      const { data } = await metricsApi.getClusterMetrics({
        start: start.toISOString(),
        end: end.toISOString(),
      })
      return data
    },
    refetchInterval: 30000,
  })

  const chartData = metrics.map((m) => ({
    time: format(new Date(m.timestamp), 'HH:mm'),
    cpu: m.cpu_usage_percent,
    memory: (m.memory_usage_bytes / m.memory_limit_bytes) * 100,
    network_rx: m.network_rx_bytes / 1024 / 1024, // MB
    network_tx: m.network_tx_bytes / 1024 / 1024, // MB
  }))

  return (
    <Card shadow="sm" p="lg" radius="md">
      <Group justify="space-between" mb="md">
        <Text size="lg" fw={600}>
          Cluster Metrics
        </Text>
        <Select
          value={timeRange}
          onChange={(value) => setTimeRange(value || '1h')}
          data={timeRanges}
          size="sm"
          w={150}
        />
      </Group>

      <Stack gap="lg">
        {/* CPU & Memory */}
        <div style={{ height: 250 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="time" />
              <YAxis />
              <Tooltip
                formatter={(value: number) => `${value.toFixed(1)}%`}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="cpu"
                stroke="var(--mantine-color-blue-6)"
                name="CPU %"
                strokeWidth={2}
                dot={false}
              />
              <Line
                type="monotone"
                dataKey="memory"
                stroke="var(--mantine-color-green-6)"
                name="Memory %"
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Network */}
        <div style={{ height: 200 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="time" />
              <YAxis />
              <Tooltip
                formatter={(value: number) => `${value.toFixed(2)} MB/s`}
              />
              <Legend />
              <Area
                type="monotone"
                dataKey="network_rx"
                stackId="1"
                stroke="var(--mantine-color-violet-6)"
                fill="var(--mantine-color-violet-2)"
                name="Network In"
              />
              <Area
                type="monotone"
                dataKey="network_tx"
                stackId="1"
                stroke="var(--mantine-color-orange-6)"
                fill="var(--mantine-color-orange-2)"
                name="Network Out"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </Stack>
    </Card>
  )
}